import type { Query } from "@tanstack/react-query";
import { freshnessOf } from "@/queries/policies";
import type { THintTargets } from "./types";

const isAccessSensitive = (query: Query) => freshnessOf(query.meta) === "access-sensitive";

/**
 * "What you may do changed" (NG-3012). The backend sends `access:self` when a
 * write changes a person's grants or their workspace membership, naming them,
 * and the notification service relays it only to their tabs (a change for more
 * than 200 people goes to the whole workspace instead). It names no read: every
 * access-sensitive entry this principal holds in the workspace may now be
 * wrong, so all of them are marked stale and the ones on screen re-read. That
 * is what lets the class reuse an answer for 60 s (contract §2.2).
 *
 * Matched by the freshness class each query declares in its `meta`, so a new
 * access-sensitive query is covered without a row of its own.
 */
export const ACCESS_HINTS: THintTargets = {
  "access:self": {
    mark: (_root, _slug, { client, scope }) =>
      void client.invalidateQueries({
        queryKey: [scope.tenant, scope.principal],
        predicate: isAccessSensitive,
        refetchType: "none",
      }),
    reread: (_root, _slug, { client, scope }) =>
      void client.refetchQueries({
        queryKey: [scope.tenant, scope.principal],
        predicate: isAccessSensitive,
        type: "active",
        stale: true,
      }),
  },
};
